/**
 * ISR Page Utilities
 *
 * Shared utilities for ISR page components to reduce boilerplate
 * and centralize common patterns.
 */

'use client';

import {useEffect} from 'react';
import {useSession} from 'next-auth/react';
import {Customer} from '__generated__/__types__';
import useConversionApi from '@/_application/modules/tracking/meta/tracking-conversion-api';
import {MetaEventList} from '@/_application/modules/tracking/meta/model/meta-conversion';
import {storeServerData} from '@/_application/modules/brand/helpers/brand-data';
import {DynamicPageServerSideProps} from '@/_application/core/types';

// ============================================================================
// TYPES
// ============================================================================

export interface IsrPageWrapperProps {
    variables?: DynamicPageServerSideProps['variables'];
    isMobile?: boolean;
    layout?: DynamicPageServerSideProps['layout'];
}

export interface TrackingConfig {
    eventType: MetaEventList;
    eventData?: string | Record<string, unknown>;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to handle common ISR page initialization
 * - Stores server data for brand helpers
 * - Returns customer from session
 *
 * @param variables - Server-side props variables
 * @returns Customer object or undefined
 */
export function useIsrPageInit(variables?: DynamicPageServerSideProps['variables']): Customer | undefined {
    const {data: session} = useSession();
    const customer = session?.user as Customer | undefined;

    useEffect(() => {
        if (variables) {
            storeServerData(variables);
        }
    }, [variables]);

    return customer;
}

/**
 * Hook to handle Meta Conversion API tracking
 * Only fires on client-side after hydration
 *
 * @param config - Tracking configuration
 * @param dependencies - Additional dependencies to trigger tracking
 */
export function useIsrTracking(
    config: TrackingConfig | null,
    dependencies: unknown[] = [],
): void {
    const {data: session} = useSession();
    const customer = session?.user as Customer | undefined;
    const {sendProcessedDataToConversionAPI} = useConversionApi();

    useEffect(() => {
        if (!config || !customer) return;

        sendProcessedDataToConversionAPI(config.eventType, config.eventData, customer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer, config?.eventType, config?.eventData, ...dependencies]);
}

/**
 * Combined hook for ISR pages - init + tracking
 *
 * @param variables - Server-side props variables
 * @param trackingConfig - Optional tracking configuration
 * @returns Customer object or undefined
 */
export function useIsrPage(
    variables?: DynamicPageServerSideProps['variables'],
    trackingConfig?: TrackingConfig | null,
): Customer | undefined {
    const customer = useIsrPageInit(variables);

    // Only track if config is provided and customer exists
    useIsrTracking(trackingConfig ?? null, []);

    return customer;
}
